import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, MessageSquare, Search, Star, Heart, BellRing, TrendingUp, Calendar, ChevronRight, Briefcase, Users, Eye, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

export default function EmployerAnalytics({ onTabChange }) {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'hiring', 'trends'

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employer/analytics`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleStatClick = (statType) => {
    switch (statType) {
      case 'searches':
        if (onTabChange) onTabChange('search');
        break;
      case 'messages':
        navigate('/messages');
        break;
      case 'reviews':
        if (onTabChange) onTabChange('reviews');
        break;
      case 'favorites':
        if (onTabChange) onTabChange('favorites');
        break;
      case 'hired':
        if (onTabChange) onTabChange('hired');
        break;
      default:
        break;
    }
  };

  const handleMessageClick = (recipientId) => {
    if (recipientId) {
      navigate(`/messages?user=${recipientId}`);
    } else {
      navigate('/messages');
    }
  };

  const handleProfileClick = (tradespersonId) => {
    navigate(`/profile/${tradespersonId}`);
  };

  if (loading) {
    return (
      <div className="card animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-slate-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const { summary, hiring, funnel, weekly_trends, top_contacts, recent_hires, recent_activity, subscription } = analytics;

  const stats = [
    {
      label: 'Searches (30d)',
      value: summary.searches_30d,
      subValue: `${summary.searches_7d} this week`,
      icon: Search,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      hoverBg: 'hover:bg-blue-100',
      action: 'searches'
    },
    {
      label: 'Messages Sent',
      value: summary.messages_sent_30d,
      subValue: `${summary.unique_contacts_30d} unique contacts`,
      icon: MessageSquare,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      hoverBg: 'hover:bg-green-100',
      action: 'messages'
    },
    {
      label: 'Active Hires',
      value: hiring?.active_hires || 0,
      subValue: `${hiring?.total_hires || 0} total`,
      icon: Briefcase,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      hoverBg: 'hover:bg-purple-100',
      action: 'hired'
    },
    {
      label: 'Favorites',
      value: summary.favorites_count,
      subValue: `${summary.active_alerts} active alerts`,
      icon: Heart,
      color: 'text-red-500',
      bgColor: 'bg-red-50',
      hoverBg: 'hover:bg-red-100',
      action: 'favorites'
    }
  ];

  // Calculate funnel conversion rates
  const viewToContactRate = funnel?.profiles_viewed > 0 
    ? Math.round((funnel.contacted / funnel.profiles_viewed) * 100) 
    : 0;
  const contactToHireRate = funnel?.contacted > 0 
    ? Math.round((funnel.hired / funnel.contacted) * 100) 
    : 0;

  return (
    <div className="space-y-6 mb-8" data-testid="employer-analytics">
      {/* Header with Tab Navigation */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-orange-500" />
            Your Activity Dashboard
          </h3>
          <div className="flex items-center gap-2">
            {/* Tab Buttons */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              {['overview', 'hiring', 'trends'].map((view) => (
                <button
                  key={view}
                  onClick={() => setActiveView(view)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                    activeView === view
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`tab-${view}`}
                >
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </button>
              ))}
            </div>
            {subscription && (
              <div className="hidden md:flex items-center gap-2 text-sm bg-green-50 px-3 py-1.5 rounded-lg">
                <span className="text-green-700 font-medium">{subscription.plan_name}</span>
                <span className="text-green-600">({subscription.days_remaining}d left)</span>
              </div>
            )}
          </div>
        </div>

        {/* Overview View */}
        {activeView === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {stats.map((stat, idx) => (
                <button
                  key={idx}
                  onClick={() => handleStatClick(stat.action)}
                  className={`${stat.bgColor} ${stat.hoverBg} rounded-xl p-4 transition-all duration-200 hover:scale-105 hover:shadow-md cursor-pointer text-left active:scale-95`}
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <stat.icon className={`h-5 w-5 ${stat.color}`} />
                      <span className="text-xs font-medium text-slate-600">{stat.label}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{stat.subValue}</p>
                </button>
              ))}
            </div>

            {/* Conversion Funnel */}
            {funnel && (funnel.profiles_viewed > 0 || funnel.contacted > 0) && (
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-5 mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-orange-500" />
                  Your Hiring Funnel (90 days)
                </h4>
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                      <Eye className="h-7 w-7 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{funnel.profiles_viewed}</p>
                    <p className="text-xs text-slate-500">Profiles Viewed</p>
                  </div>
                  <div className="flex flex-col items-center px-2">
                    <ArrowRight className="h-5 w-5 text-slate-300" />
                    <span className="text-xs text-slate-400 mt-1">{viewToContactRate}%</span>
                  </div>
                  <div className="text-center flex-1">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                      <MessageSquare className="h-7 w-7 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{funnel.contacted}</p>
                    <p className="text-xs text-slate-500">Contacted</p>
                  </div>
                  <div className="flex flex-col items-center px-2">
                    <ArrowRight className="h-5 w-5 text-slate-300" />
                    <span className="text-xs text-slate-400 mt-1">{contactToHireRate}%</span>
                  </div>
                  <div className="text-center flex-1">
                    <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                      <Briefcase className="h-7 w-7 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{funnel.hired}</p>
                    <p className="text-xs text-slate-500">Hired</p>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {recent_activity && recent_activity.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Recent Messages Sent
                </h4>
                <div className="space-y-2">
                  {recent_activity.map((activity, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleMessageClick(activity.recipient_id)}
                      className="w-full flex items-center justify-between py-3 px-4 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm transition-all duration-200 cursor-pointer active:scale-[0.99] hover:shadow-sm"
                      data-testid={`recent-activity-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <MessageSquare className="h-4 w-4 text-slate-400" />
                        <span className="font-medium text-slate-700">{activity.recipient_name}</span>
                        <span className="text-slate-500 truncate max-w-[150px] sm:max-w-[200px]">{activity.content_preview}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {activity.sent_at ? new Date(activity.sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : 'N/A'}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Hiring View */}
        {activeView === 'hiring' && (
          <>
            {/* Hiring Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{hiring?.active_hires || 0}</p>
                <p className="text-sm text-green-700">Active Hires</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{hiring?.completed_30d || 0}</p>
                <p className="text-sm text-blue-700">Completed (30d)</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">{hiring?.total_hires || 0}</p>
                <p className="text-sm text-purple-700">Total Hires</p>
              </div>
            </div>

            {/* Top Contacts */}
            {top_contacts && top_contacts.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Top Tradespeople You've Contacted
                </h4>
                <div className="space-y-2">
                  {top_contacts.map((contact, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleProfileClick(contact.tradesperson_id)}
                      className="w-full flex items-center justify-between py-3 px-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-all"
                      data-testid={`top-contact-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                          <span className="text-orange-600 font-bold">{contact.name?.charAt(0) || '?'}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-slate-900">{contact.name}</p>
                          <p className="text-xs text-slate-500">{contact.trade}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-700">{contact.message_count} messages</p>
                          {contact.rating > 0 && (
                            <div className="flex items-center gap-1 text-xs text-amber-600">
                              <Star className="h-3 w-3 fill-amber-400" />
                              {contact.rating.toFixed(1)}
                            </div>
                          )}
                        </div>
                        {contact.hired && (
                          <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Hired
                          </span>
                        )}
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Hires */}
            {recent_hires && recent_hires.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Recent Hires
                </h4>
                <div className="space-y-2">
                  {recent_hires.map((hire, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg"
                      data-testid={`recent-hire-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${hire.status === 'active' ? 'bg-green-500' : 'bg-slate-400'}`}></div>
                        <div>
                          <p className="font-medium text-slate-900">{hire.tradesperson_name}</p>
                          <p className="text-xs text-slate-500">{hire.trade}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          hire.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {hire.status === 'active' ? 'In Progress' : 'Completed'}
                        </span>
                        <p className="text-xs text-slate-400 mt-1">
                          {hire.start_date ? new Date(hire.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State for Hiring */}
            {(!recent_hires || recent_hires.length === 0) && (!top_contacts || top_contacts.length === 0) && (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Briefcase className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No hiring activity yet</p>
                <p className="text-sm text-slate-400 mt-1">Start searching and contacting tradespeople to build your hiring history</p>
              </div>
            )}
          </>
        )}

        {/* Trends View */}
        {activeView === 'trends' && (
          <>
            {/* Weekly Trends Chart */}
            {weekly_trends && weekly_trends.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Weekly Activity (Last 4 Weeks)
                </h4>
                
                {/* Simple Bar Chart */}
                <div className="space-y-4">
                  {weekly_trends.map((week, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 font-medium">{week.week_start}</span>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            {week.searches} searches
                          </span>
                          <span className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            {week.messages} messages
                          </span>
                          <span className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                            {week.hires} hires
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1 h-8">
                        {/* Searches bar */}
                        <div 
                          className="bg-blue-500 rounded-l-md transition-all"
                          style={{ width: `${Math.max(5, (week.searches / Math.max(...weekly_trends.map(w => w.searches + w.messages + w.hires), 1)) * 100)}%` }}
                        ></div>
                        {/* Messages bar */}
                        <div 
                          className="bg-green-500 transition-all"
                          style={{ width: `${Math.max(5, (week.messages / Math.max(...weekly_trends.map(w => w.searches + w.messages + w.hires), 1)) * 100)}%` }}
                        ></div>
                        {/* Hires bar */}
                        <div 
                          className="bg-purple-500 rounded-r-md transition-all"
                          style={{ width: `${Math.max(5, (week.hires / Math.max(...weekly_trends.map(w => w.searches + w.messages + w.hires), 1)) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Legend */}
                <div className="flex items-center justify-center gap-6 mt-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-slate-600">Searches</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-slate-600">Messages</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span className="text-slate-600">Hires</span>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">{summary.searches_30d}</p>
                <p className="text-xs text-slate-500">Searches (30d)</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">{summary.messages_sent_30d}</p>
                <p className="text-xs text-slate-500">Messages (30d)</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">{summary.reviews_left_total}</p>
                <p className="text-xs text-slate-500">Reviews Left</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-slate-900">{hiring?.total_hires || 0}</p>
                <p className="text-xs text-slate-500">Total Hires</p>
              </div>
            </div>
          </>
        )}

        {/* Empty State */}
        {activeView === 'overview' && (!recent_activity || recent_activity.length === 0) && summary.messages_sent_30d === 0 && (
          <div className="text-center py-6 bg-slate-50 rounded-lg">
            <Search className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">Start searching to see your activity here</p>
          </div>
        )}
      </div>
    </div>
  );
}
